import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { HMSRoomProvider } from "@100mslive/react-sdk";
import { ArrowRight, VideoCameraSlash } from "@phosphor-icons/react";
import ContentLoader from "../../components/shared/ContentLoader";
import {
  selectClinicId,
  selectTherapistName,
} from "../../store/slices/authSlice";
import { getVideoMeetingHostToken } from "../../firebase";
import VideoMeetingRoom from "../../components/videoMeeting/VideoMeetingRoom";
import "./VideoMeetingPage.css";

const ERROR_MESSAGES = {
  feature_disabled: "פגישות וידאו אינן מופעלות בקליניקה. ניתן להפעיל אותן בהגדרות הקליניקה.",
  not_online_meeting: "הפגישה הזו אינה מוגדרת כפגישת וידאו אונליין.",
  monthly_limit: "מכסת דקות הווידאו החודשית של הקליניקה נוצלה במלואה. המכסה מתחדשת בתחילת החודש.",
};

/**
 * Therapist-side video call page (/dashboard/video-meeting/:appointmentId).
 * Fetches a 100ms host token (server enforces clinic membership, feature flag
 * and monthly quota) and renders the shared call UI.
 */
function VideoMeetingPage() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const clinicId = useSelector(selectClinicId);
  const therapistName = useSelector(selectTherapistName);

  const [tokenData, setTokenData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!clinicId || !appointmentId) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await getVideoMeetingHostToken(clinicId, appointmentId);
        if (cancelled) return;
        if (result?.success) {
          setTokenData(result);
        } else {
          setErrorMessage(
            ERROR_MESSAGES[result?.errorType] ||
              "לא ניתן לטעון את פגישת הווידאו. נסו שוב."
          );
        }
      } catch (error) {
        console.error("[CK-3970] Error loading video meeting page:", {
          errorCode: "CK-3970",
          errorMessage: error.message,
          appointmentId,
          timestamp: new Date().toISOString(),
        });
        if (!cancelled) {
          setErrorMessage("שגיאה בטעינת פגישת הווידאו. נסו שוב.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clinicId, appointmentId]);

  const goBack = () => navigate("/dashboard/calendar");

  return (
    <div className="video-meeting-page">
      <div className="video-meeting-topbar">
        <button type="button" className="video-meeting-back" onClick={goBack}>
          <ArrowRight size={16} /> חזרה ליומן
        </button>
        {tokenData?.patientName && (
          <span className="video-meeting-topbar-title">
            פגישת וידאו עם {tokenData.patientName}
          </span>
        )}
        {tokenData?.success && (
          <span className="video-meeting-quota">
            {Math.max(0, tokenData.limitMinutes - tokenData.usedMinutes)} דק׳
            וידאו נותרו החודש
          </span>
        )}
      </div>

      {isLoading ? (
        <ContentLoader fill message="טוען את פגישת הווידאו..." />
      ) : errorMessage ? (
        <div className="video-meeting-state">
          <VideoCameraSlash size={30} weight="fill" />
          <span>{errorMessage}</span>
          <button type="button" className="video-meeting-back-btn" onClick={goBack}>
            חזרה ליומן
          </button>
        </div>
      ) : (
        <HMSRoomProvider>
          <VideoMeetingRoom
            authToken={tokenData.token}
            userName={therapistName || "מטפל/ת"}
            title={
              tokenData.patientName
                ? `פגישת וידאו עם ${tokenData.patientName}`
                : "פגישת וידאו"
            }
            subtitle="המטופל מצטרף דרך הקישור שנשלח אליו ב-SMS"
            onLeft={goBack}
          />
        </HMSRoomProvider>
      )}
    </div>
  );
}

export default VideoMeetingPage;
